import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { addExternalToLibrary, sendToMiroQueue } from '@shared/services'
import { markStockUsed } from '@shared/services/stockService'
import { marketOf } from '@shared/services/amazonService'
import {
  keepWebImages,
  searchUnsplash,
  searchWebImages,
  type Country,
  type FoundImage,
  type PickedImage,
} from '@shared/services/webImageService'
import { WwLoaderFill } from '@shared/components/WwLoader'
import type { MiroSent } from '../hooks/useMiroSendProgress'
import { useMiroSendProgress } from '../hooks/useMiroSendProgress'
import { useClipSettled } from '../hooks/useClipSettled'
import { putClip } from '@shared/services/projectClip'
import { usePaintSelection } from '@shared/hooks/usePaintSelection'
import { GooeyBackground } from '../components/GooeyBackground'
import { MarketPicker } from '../components/MarketPicker'
import { JustifiedRows, usePictureRatios } from '../components/JustifiedRows'
import './amazon.css'
import './web-images.css'

/**
 * Google Images (Huanrui, 2026-10-14)
 * -------------------------------------------------------------
 * The Amazon page, for Google's image results: search, tick pictures (a
 * click, or a sweep across cards), Send to Miro or Copy to project. "View
 * larger" opens one picture over the page, with the page it came from.
 *
 * A search is one request of the Scale SERP plan, and so is each Load more;
 * the same search within a week is free (the Worker keeps it). Which is why,
 * as on Amazon, more results come from a button and not from scrolling.
 *
 * The grid shows each picture straight from its site. Some sites refuse to
 * be shown elsewhere; those cards simply drop out.
 */

/**
 * External Library (2026-10-14): this page also draws Unsplash, in the same
 * shape. Unsplash's rules (routes/stock.ts): its pictures are shown from its
 * own addresses, the photographer and Unsplash are credited, and a picture
 * that is used -- sent to Miro, copied to a project -- is reported once.
 */
export type ImageSource = 'google' | 'unsplash'

const creditOf = (found: FoundImage): string =>
  found.author ? `Photo by ${found.author} on Unsplash` : ''

const pickOf = (found: FoundImage, source: ImageSource): PickedImage => ({
  url: found.url,
  thumb: found.thumb,
  title: found.title,
  page: found.page,
  site: found.site,
  source,
  ...(source === 'unsplash'
    ? { credit: creditOf(found), downloadLocation: found.downloadLocation }
    : {}),
})

const BAR_WORDS = {
  amazon: 'Search Amazon for a product',
  google: 'Search Google Images',
  unsplash: 'Search Unsplash',
} as const

/** One card per picture. */
const foundKey = (found: { url: string }) => found.url

export function WebImagesPage({
  source = 'google',
  lead,
  initialTerm = '',
  onTyped,
  autoSearch = false,
  onSearched,
  onSubmitInstead,
  barSource,
  market: sharedCountry,
  onMarket,
}: {
  source?: ImageSource
  /** In front of the search box: the External Library's source menu. */
  lead?: ReactNode
  /** In the box when the page opens -- the words carried over from another source. Not searched until Search is pressed. */
  initialTerm?: string
  /** Told the words in the box as they are typed, so a switch of source can carry them over. */
  onTyped?: (term: string) => void
  /** Search `initialTerm` as soon as the page opens, already docked (a source switched, then Search). */
  autoSearch?: boolean
  /** Told when this page has searched, so the page around it knows its results are worth keeping. */
  onSearched?: () => void
  /**
   * Search is pressed while another source is chosen in the menu: the words go
   * there instead, and this page -- its results still on screen until then --
   * makes way (External Library, 2026-10-14).
   */
  onSubmitInstead?: (term: string) => void
  /**
   * The source the bar is drawn for: the one chosen in the menu, which may
   * not be this page's until Search is pressed (2026-10-14). Its words and
   * whether it has a country follow the choice at once.
   */
  barSource?: 'amazon' | 'google' | 'unsplash'
  /** The country, kept by the page around this one so a switch carries it. */
  market?: Country
  onMarket?: (next: Country) => void
} = {}) {
  const unsplash = source === 'unsplash'
  const named = unsplash ? 'Unsplash' : 'Google'
  const [ownCountry, setOwnCountry] = useState<Country>('us')
  const country = sharedCountry ?? ownCountry
  const setCountry = onMarket ?? setOwnCountry
  const barFor = barSource ?? source
  const [draft, setDraft] = useState(initialTerm)

  const [asked, setAsked] = useState<{ country: Country; term: string } | null>(null)
  const [results, setResults] = useState<FoundImage[]>([])
  const [page, setPage] = useState(0)
  const [more, setMore] = useState(false)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const [creditsLeft, setCreditsLeft] = useState<number | null>(null)
  /** Pictures whose site would not let them be shown here. */
  const [broken, setBroken] = useState<Set<string>>(new Set())

  /** Said under the grid when Google has nothing more for this search. */
  const [endNote, setEndNote] = useState('')
  /** The results as they stand, for telling whether a page added anything. */
  const resultsNow = useRef<FoundImage[]>([])
  useEffect(() => {
    resultsNow.current = results
  }, [results])

  const [picked, setPicked] = useState<Map<string, PickedImage>>(new Map())
  const [open, setOpen] = useState<number | null>(null)

  /* ---------- searching ---------- */

  const run = useCallback(async (wanted: { country: Country; term: string }, at: number) => {
    setSearching(true)
    setError('')
    setEndNote('')
    if (at === 1) {
      setResults([])
      setPage(0)
      setMore(false)
      setBroken(new Set())
    }
    try {
      const found = unsplash
        ? await searchUnsplash(wanted.term, at)
        : await searchWebImages(wanted.country, wanted.term, at)
      setAsked(wanted)
      setPage(found.page)
      // A page that adds nothing new -- empty, or the same pictures again --
      // is the end of what Google will give for this search: say so, rather
      // than the button simply going away.
      const known = new Set(at === 1 ? [] : resultsNow.current.map((one) => one.url))
      const fresh = found.results.filter((one) => !known.has(one.url)).length
      if (at > 1 && fresh === 0) {
        setMore(false)
        setEndNote(`${named} has no more pictures for this search.`)
      } else {
        setMore(found.more)
        if (!found.more && at > 1) setEndNote(`That is everything ${named} has for this search.`)
      }
      if (found.creditsLeft !== null) setCreditsLeft(found.creditsLeft)
      setResults((was) => {
        const seen = new Set(at === 1 ? [] : was.map((one) => one.url))
        const next = at === 1 ? [] : [...was]
        for (const one of found.results) {
          if (seen.has(one.url)) continue
          seen.add(one.url)
          next.push(one)
        }
        return next
      })
      if (at === 1 && found.results.length === 0) setError(`${named} found no pictures for that`)
    } catch (caught) {
      const said =
        caught instanceof Error
          ? caught.name === 'TimeoutError'
            ? `${named} took too long to answer. Try again in a moment.`
            : caught.message
          : 'The search did not work'
      // A first search says so under the bar; a Load more says so beside its
      // button, at the bottom where the eye is -- it used to go to the top of
      // the page, out of sight, and the button just came back.
      if (at === 1) setError(said)
      else setEndNote(`Page ${at} did not load: ${said}`)
    } finally {
      setSearching(false)
    }
    // `unsplash` and `named` follow the page's one source, fixed for its life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The bar in the middle of the empty page, gliding up at the first search
  // -- the Amazon page's opening, the same code.
  const opensSearching = autoSearch && Boolean(initialTerm.trim())
  const [docked, setDocked] = useState(opensSearching)
  const bar = useRef<HTMLFormElement | null>(null)
  const barWas = useRef<DOMRect | null>(null)
  const [glow, setGlow] = useState(!opensSearching)
  useEffect(() => {
    if (docked) return
    const root = document.documentElement
    const was = root.style.overflow
    root.style.overflow = 'hidden'
    return () => {
      root.style.overflow = was
    }
  }, [docked])
  useEffect(() => {
    if (!docked) return
    const timer = window.setTimeout(() => setGlow(false), 800)
    return () => window.clearTimeout(timer)
  }, [docked])

  const submit = () => {
    const term = draft.trim()
    if (!term || searching) return
    if (onSubmitInstead) {
      onSubmitInstead(term)
      return
    }
    onSearched?.()
    if (!docked) {
      barWas.current = bar.current?.getBoundingClientRect() ?? null
      setDocked(true)
    }
    void run({ country, term }, 1)
  }

  // Opened by Search on another source's page: searched at once.
  useEffect(() => {
    const term = initialTerm.trim()
    if (!opensSearching || !term) return
    onSearched?.()
    void run({ country, term }, 1)
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    const el = bar.current
    const was = barWas.current
    barWas.current = null
    if (!docked || !el || !was) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const now = el.getBoundingClientRect()
    const dx = was.left - now.left
    const dy = was.top - now.top
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
    el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
      duration: 620,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    })
  }, [docked])

  /* ---------- ticking ---------- */

  const toggle = useCallback((found: FoundImage) => {
    setPicked((was) => {
      const next = new Map(was)
      if (next.has(found.url)) next.delete(found.url)
      else next.set(found.url, pickOf(found, source))
      return next
    })
  }, [])

  const setSome = useCallback((list: FoundImage[], on: boolean) => {
    setPicked((was) => {
      const next = new Map(was)
      for (const one of list) {
        if (on) next.set(one.url, pickOf(one, source))
        else next.delete(one.url)
      }
      return next
    })
  }, [])

  const onPaint = useCallback((found: FoundImage, on: boolean) => setSome([found], on), [setSome])
  const paint = usePaintSelection<FoundImage>(onPaint)

  const shown = useMemo(() => results.filter((one) => !broken.has(one.url)), [results, broken])
  // Unsplash's rows (Google keeps its square cards): sizes are given; the
  // odd one without is read as it is listed.
  const unsized = useMemo(
    () => (unsplash ? shown.filter((one) => !(one.width && one.height)).map((one) => one.thumb) : []),
    [shown, unsplash],
  )
  const ratios = usePictureRatios(unsized)
  const pickFirst = (count: number) => setSome(shown.slice(0, count), true)
  const selectSteps = [20, 50].filter((n) => n < shown.length)

  const dropBroken = (url: string) =>
    setBroken((was) => (was.has(url) ? was : new Set(was).add(url)))

  /* ---------- the tray ---------- */

  const [sending, setSending] = useState(false)
  /**
   * Also into the Internal Library (Audit), as they are (Huanrui, 2026-11-02).
   * Google only; off every time, unticked again after each Send or Copy.
   */
  const [toLibrary, setToLibrary] = useState(false)
  const fileIfTicked = (pictures: { url: string; title: string; page: string }[]) => {
    if (!toLibrary || unsplash) return
    addExternalToLibrary('google', asked?.term ?? '', pictures)
    setToLibrary(false)
  }
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [sent, setSent] = useState<MiroSent | null>(null)
  useMiroSendProgress(sent, setStatus, () => setSent(null))
  const [copied, setCopied] = useState<{ token: string; text: string } | null>(null)
  useClipSettled(copied?.token ?? '', () => {
    setStatus((now) => (now && now.text === copied?.text ? null : now))
    setCopied(null)
  })
  const dismiss = () => {
    setStatus(null)
    setSent(null)
    setCopied(null)
  }

  const send = async () => {
    if (sending || picked.size === 0) return
    setSending(true)
    setStatus({ kind: 'ok', text: `Copying ${picked.size} from ${unsplash ? 'Unsplash' : 'the web'}…` })
    try {
      const all = [...picked.values()]
      void markStockUsed(all.map((one) => one.downloadLocation ?? '').filter(Boolean))
      fileIfTicked(all.map((one) => ({ url: one.url, title: one.title, page: one.page })))
      const kept = await keepWebImages(all)
      if (kept.refs.length === 0) throw new Error('None of them could be copied from their sites')
      const queue = await sendToMiroQueue('web', kept.refs)
      setPicked(new Map())
      setSent({ at: queue.createdAt, items: queue.items })
      const queued = queue.items.length
      setStatus({
        kind: kept.failed > 0 ? 'err' : 'ok',
        text:
          (kept.failed > 0 ? `${kept.failed} could not be copied from their sites. ` : '') +
          `${queued} image${queued === 1 ? '' : 's'} ready. Open the WonderWorks app on a Miro board to place them.`,
      })
    } catch (caught) {
      setStatus({ kind: 'err', text: caught instanceof Error ? caught.message : 'Could not send to Miro' })
    } finally {
      setSending(false)
    }
  }

  const copyToProject = () => {
    if (picked.size === 0) return
    try {
      void markStockUsed([...picked.values()].map((one) => one.downloadLocation ?? '').filter(Boolean))
      fileIfTicked([...picked.values()].map((one) => ({ url: one.url, title: one.title, page: one.page })))
      const clip = putClip(
        unsplash ? 'Unsplash' : 'Google Images',
        [...picked.values()].map((one) => ({ kind: 'web' as const, ...one })),
      )
      const text = `${picked.size} copied. Open any project and they will be offered to its canvas.`
      setStatus({ kind: 'ok', text })
      setCopied({ token: clip.token, text })
      setPicked(new Map())
    } catch (caught) {
      console.error('could not write the copy down', caught)
      setStatus({ kind: 'err', text: 'This browser would not keep the copy' })
    }
  }

  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const shownTray = useMemo(() => [...picked.entries()].slice(0, 48), [picked])

  /* ---------- drawing ---------- */

  /**
   * One picture's card. Given a size, it is a tile in Unsplash's rows (the
   * picture fills it, the credit shows on hover); without one, Google's
   * square card with the site and title under the picture.
   */
  const card = (found: FoundImage, size?: { width: number; height: number }) => {
    const on = picked.has(found.url)
    return (
      <div
        key={found.url}
        className={`amz-card${on ? ' amz-card--on' : ''}`}
        style={size}
        title={size ? [found.author ? `${found.author} · Unsplash` : found.site, found.title].filter(Boolean).join(' -- ') : undefined}
        role="button"
        tabIndex={0}
        aria-pressed={on}
        aria-label={`${on ? 'Unpick' : 'Pick'} ${found.title || found.site}`}
        onPointerDown={(event) => paint.onPointerDown(found, on, event)}
        onPointerEnter={() => paint.onPointerEnter(found, on)}
        onClick={() => {
          if (paint.shouldSwallowClick()) return
          toggle(found)
        }}
        onKeyDown={(event) => {
          if (event.key === ' ') {
            event.preventDefault()
            toggle(found)
          }
          if (event.key === 'Enter') setOpen(shown.indexOf(found))
        }}
      >
        <div className="amz-card__media">
          <img
            src={found.thumb}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onError={() => dropBroken(found.url)}
          />
          <button
            type="button"
            className="amz-card__open"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              setOpen(shown.indexOf(found))
            }}
          >
            View larger
          </button>
        </div>
        <span className="amz-tick" aria-hidden="true" data-on={on}>
          ✓
        </span>
        {size ? (
          found.author ? <span className="amz-card__credit">{found.author} · Unsplash</span> : null
        ) : (
          <div className="amz-card__text">
            {found.author ? (
              <span className="amz-card__brand">{found.author} · Unsplash</span>
            ) : found.site ? (
              <span className="amz-card__brand">{found.site}</span>
            ) : null}
            <span className="amz-card__title">{found.title}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="amz wimg">
      {glow ? (
        <div className={`amz__glow${docked ? ' amz__glow--off' : ''}`} aria-hidden="true">
          <GooeyBackground />
        </div>
      ) : null}
      <div className={`amz__stage${docked ? '' : ' amz__stage--idle'}`}>
        <form
          className={`amz__bar${docked ? ' amz__bar--docked' : ''}`}
          ref={bar}
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          {lead}
          <svg className="amz__glass-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M15.5 15.5 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            className="amz__search"
            type="search"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              onTyped?.(event.target.value)
            }}
            placeholder={BAR_WORDS[barFor]}
            aria-label={BAR_WORDS[barFor]}
            autoFocus
          />
          {barFor === 'unsplash' ? null : <MarketPicker value={country} onChange={(next) => setCountry(next)} />}
          <button type="submit" className="amz__go" disabled={searching || !draft.trim()}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>
      </div>

      {error ? <p className="amz__error">{error}</p> : null}

      {asked && shown.length > 0 ? (
        <div className="amz__metarow">
          <div className="web-select" role="group" aria-label="Pick pictures">
            <span className="web-select__label">Select</span>
            {selectSteps.map((count) => (
              <button key={count} type="button" className="web-select__step" onClick={() => pickFirst(count)}>
                {count}
              </button>
            ))}
            <button type="button" className="web-select__step" onClick={() => pickFirst(shown.length)}>
              All
            </button>
            <span className="web-select__of">in the page</span>
          </div>
          <p className="amz__meta">
            {shown.length} pictures for “{asked.term}” ·{' '}
            {unsplash ? 'Unsplash' : `Google ${marketOf(asked.country).label}`}
            {creditsLeft !== null ? (
              <span className="wimg__credits"> · {creditsLeft} searches left on the plan</span>
            ) : null}
          </p>
        </div>
      ) : null}

      {searching && results.length === 0 ? (
        <div className="amz__waiting">
          <WwLoaderFill label={`Searching ${named}`} size="large" />
        </div>
      ) : null}

      {/* Google: the square cards with site and title under them. Unsplash:
          rows like the Internal Library's (Huanrui, 2026-10-22), each picture
          at its own shape, no white box round it, the photographer's credit
          on the picture on hover and site and title as the tooltip. */}
      {unsplash ? (
        <JustifiedRows
          items={shown}
          keyOf={foundKey}
          ratio={(found) => (found.width && found.height ? found.width / found.height : (ratios.get(found.thumb) ?? null))}
          className={`amz__rows${paint.active ? ' amz__grid--painting' : ''}`}
          render={(found, cardWidth, cardHeight) => card(found, { width: cardWidth, height: cardHeight })}
        />
      ) : (
        <div className={`amz__grid${paint.active ? ' amz__grid--painting' : ''}`}>
          {shown.map((found) => card(found))}
        </div>
      )}

      {searching && results.length > 0 ? (
        <div className="amz__waiting amz__waiting--more">
          <WwLoaderFill label="Loading more" size="large" />
        </div>
      ) : null}

      {asked && more && !searching && results.length > 0 ? (
        <div className="amz__more">
          <button type="button" className="ww-btn ww-btn--secondary" onClick={() => void run(asked, page + 1)}>
            Load more
          </button>
          <span>Page {page} · one more page, one search</span>
        </div>
      ) : null}

      {endNote && !searching ? (
        <div className="amz__more">
          <span>{endNote}</span>
        </div>
      ) : null}

      {scrolled && open === null ? (
        <button
          type="button"
          className={`web-totop${picked.size > 0 ? ' web-totop--raised' : ''}`}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span aria-hidden="true">&#8593;</span>
          Top
        </button>
      ) : null}

      {open !== null && shown[open] ? (
        <ImageView
          list={shown}
          at={open}
          picked={picked}
          tray={picked.size > 0}
          onMove={setOpen}
          onToggle={toggle}
          onClose={() => setOpen(null)}
        />
      ) : null}

      {picked.size > 0 ? (
        <div className="web-tray amz__tray" role="region" aria-label="Picked pictures">
          {status ? (
            <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
              <span>{status.text}</span>
              <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
                Dismiss
              </button>
            </div>
          ) : null}
          <div className="web-tray__row">
            <span className="web-tray__count">
              {picked.size} <span>picked</span>
            </span>
            <div className="web-tray__thumbs">
              {shownTray.map(([key, one]) => (
                <button
                  key={key}
                  type="button"
                  className="web-tray__thumb amz__tray-thumb"
                  title="Remove"
                  onClick={() =>
                    setPicked((was) => {
                      const next = new Map(was)
                      next.delete(key)
                      return next
                    })
                  }
                >
                  <img src={one.thumb} alt="" loading="lazy" referrerPolicy="no-referrer" />
                  <span className="web-tray__remove">×</span>
                </button>
              ))}
              {picked.size > shownTray.length ? (
                <span className="web-tray__more">+{picked.size - shownTray.length}</span>
              ) : null}
            </div>
            <div className="web-tray__actions">
              <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={() => setPicked(new Map())}>
                Clear
              </button>
              <button
                type="button"
                className="ww-btn ww-btn--primary ww-btn--sm"
                disabled={sending}
                onClick={() => void send()}
              >
                {sending ? 'Sending…' : 'Send to Miro'}
              </button>
              <button type="button" className="ww-btn ww-btn--secondary ww-btn--sm" onClick={copyToProject}>
                Copy to project
              </button>
              {unsplash ? null : (
                <label className="web-tray__library" title="The pictures also go into Audit, as they are, tagged by what was searched">
                  <input type="checkbox" checked={toLibrary} onChange={(event) => setToLibrary(event.target.checked)} />
                  Also add to Internal Library
                </label>
              )}
            </div>
          </div>
        </div>
      ) : status ? (
        <div className="web-tray amz__tray" role="status">
          <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
            <span>{status.text}</span>
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default WebImagesPage

/* ------------------------------------------------------------------ *
 * One picture, larger
 * ------------------------------------------------------------------ */

/**
 * The picture as large as the left of the screen allows, where it came from
 * on the right. The arrows walk through the results; Escape closes. The
 * picture at its own size is asked for; Google's small copy stays until it
 * arrives, and stays for good if the site will not send it.
 */
function ImageView({
  list,
  at,
  picked,
  tray,
  onMove,
  onToggle,
  onClose,
}: {
  list: FoundImage[]
  at: number
  picked: Map<string, PickedImage>
  tray: boolean
  onMove: (next: number) => void
  onToggle: (found: FoundImage) => void
  onClose: () => void
}) {
  const found = list[at]
  const [full, setFull] = useState<'loading' | 'ok' | 'failed'>('loading')
  useEffect(() => setFull('loading'), [found.url])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onMove((at + 1) % list.length)
      if (event.key === 'ArrowLeft') onMove((at - 1 + list.length) % list.length)
      if (event.key === ' ') {
        event.preventDefault()
        onToggle(found)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [at, found, list.length, onClose, onMove, onToggle])

  useEffect(() => {
    const before = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = before
    }
  }, [])

  const on = picked.has(found.url)

  return (
    <div className={`amz-view${tray ? ' amz-view--tray' : ''}`} role="dialog" aria-modal="true" aria-label={found.title}>
      <div className="amz-view__stage">
        {full !== 'ok' ? (
          <img className="amz-view__big" src={found.thumb} alt="" referrerPolicy="no-referrer" />
        ) : null}
        {full !== 'failed' ? (
          <img
            key={found.url}
            className={`amz-view__big${full === 'ok' ? '' : ' wimg__hidden'}`}
            src={found.url}
            alt={found.title}
            referrerPolicy="no-referrer"
            onLoad={() => setFull('ok')}
            onError={() => setFull('failed')}
          />
        ) : null}
        <button
          type="button"
          className={`amz-view__pick${on ? ' amz-view__pick--on' : ''}`}
          aria-pressed={on}
          onClick={() => onToggle(found)}
        >
          {on ? '✓ Picked' : 'Pick this picture'}
        </button>
      </div>

      <aside className="amz-view__side">
        <header className="amz-view__head">
          {found.author ? (
            // Unsplash's credit, as they ask for it: the photographer and
            // Unsplash, each linked.
            <span className="amz-view__brand">
              Photo by{' '}
              <a href={found.authorUrl} target="_blank" rel="noreferrer noopener">
                {found.author}
              </a>{' '}
              on{' '}
              <a href="https://unsplash.com/?utm_source=wonderworks&utm_medium=referral" target="_blank" rel="noreferrer noopener">
                Unsplash
              </a>
            </span>
          ) : found.site ? (
            <span className="amz-view__brand">{found.site}</span>
          ) : null}
          <h2 className="amz-view__title">{found.title || 'Untitled'}</h2>
          <div className="amz-view__row">
            <span className="amz-view__count">
              {at + 1} of {list.length}
              {found.width && found.height ? ` · ${found.width} × ${found.height}` : ''}
              {full === 'failed' ? ' · small copy only' : ''}
            </span>
          </div>
          <div className="wimg__actions">
            {found.page ? (
              <a className="ww-btn ww-btn--secondary ww-btn--sm" href={found.page} target="_blank" rel="noreferrer noopener">
                Open the page ↗
              </a>
            ) : null}
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={() => onMove((at - 1 + list.length) % list.length)}>
              ← Previous
            </button>
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={() => onMove((at + 1) % list.length)}>
              Next →
            </button>
          </div>
          <p className="wimg__note">
            {found.author
              ? 'Free to use under the Unsplash licence. Keep the credit with it.'
              : 'Found on the web; it belongs to whoever made it. For reference inside WovenWorks only.'}
          </p>
        </header>
      </aside>

      <button type="button" className="amz-view__close" onClick={onClose} aria-label="Close" title="Close (Esc)">
        ×
      </button>
    </div>
  )
}
